import React from 'react';
import { Link } from 'react-router-dom';
import {
  Brain,
  Activity,
  Target,
  Lightbulb,
  Shield,
  Globe,
  Mail,
  Linkedin,
  FileDown,
  ArrowRight,
} from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  const achievements = [
    { value: '20+', title: 'Years of Innovation', desc: 'Medical device entrepreneur & pioneer' },
    { value: '15', title: 'Patents Held', desc: 'Lead inventor across medical devices & MEMS' },
    { value: 'Global', title: 'Regulatory Expert', desc: 'FDA, TGA, CE Mark expertise' },
    { value: '3', title: 'Continents', desc: 'Worldwide technology impact' },
  ];

  const expertiseCards = [
    {
      icon: Brain,
      title: 'AI & Machine Learning',
      desc: 'Deep expertise in building intelligent systems that solve complex problems and drive measurable outcomes.',
      bullets: [
        'Machine Learning & Deep Learning',
        'Natural Language Processing',
        'Computer Vision & Image Analysis',
        'Predictive Analytics',
      ],
    },
    {
      icon: Activity,
      title: 'Medical Device Innovation',
      desc: 'End-to-end experience bringing life-changing medical devices from concept through regulatory approval to market.',
      bullets: [
        'End-to-End Device Development',
        'Regulatory Strategy & Submissions',
        'Quality Management Systems',
        'Clinical Evaluation & Trials',
      ],
    },
    {
      icon: Target,
      title: 'Strategic Leadership',
      desc: 'Proven ability to align technology vision with business strategy and deliver results at scale.',
      bullets: [
        'C-Suite Technology Advisory',
        'IP Strategy & Patent Portfolio',
        'Technology Roadmapping',
        'Digital Transformation',
      ],
    },
  ];

  const values = [
    {
      icon: Lightbulb,
      title: 'Innovation First',
      desc: 'We push boundaries and embrace emerging technologies to deliver solutions that set new standards in the industry.',
    },
    {
      icon: Shield,
      title: 'Quality Always',
      desc: 'Rigorous quality processes and regulatory excellence are embedded in everything we do, from concept to commercialisation.',
    },
    {
      icon: Globe,
      title: 'Global Perspective',
      desc: 'With experience spanning three continents, we bring a truly international lens to every engagement and strategy.',
    },
  ];

  return (
    <>
      <SEO
        title="About"
        description="Meet Dr. Julian Shapley — medical device entrepreneur, AI practitioner, and founder of P2P Digital. Over 25 years of experience in medtech, regulatory strategy, and digital health."
        path="/about"
      />
    <div>

      {/* ── HERO ── */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Glow orbs */}
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[128px] pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-indigo-500/20 rounded-full blur-[128px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <p className="uppercase tracking-[0.25em] text-cyan-400 font-mono text-sm mb-4">
            About Us
          </p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight">
            Meet the <br />
            <span className="bg-gradient-to-r from-cyan-400 to-indigo-400 bg-clip-text text-transparent">
              Mind
            </span>{' '}
            Behind P2P Digital
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-slate-400 leading-relaxed">
            Where deep scientific expertise meets bold digital vision — translating decades of innovation into real-world impact for every client we serve.
          </p>
        </div>
      </section>

      {/* ── LEADERSHIP ── */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">
            {/* Left — photo card */}
            <div className="lg:col-span-2">
              <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] backdrop-blur-md p-4">
                <img
                  src="/images/julian-shapley.jpg"
                  alt="Dr. Julian Shapley — Founder & Principal Consultant at P2P Digital"
                  className="h-96 w-full rounded-xl object-cover object-top"
                />
                <div className="mt-4 text-center pb-2">
                  <h3 className="text-white font-display font-bold text-lg">Dr. Julian Shapley</h3>
                  <p className="text-cyan-400 font-mono text-xs mt-1">Founder & Principal Consultant</p>
                </div>
              </div>
            </div>

            {/* Right — bio */}
            <div className="lg:col-span-3">
              <h2 className="text-3xl font-display font-bold text-white">Dr. Julian Shapley</h2>
              <p className="text-cyan-400 font-mono text-sm mt-1">Founder & Principal Consultant</p>

              <div className="mt-6 space-y-4 text-slate-300 leading-relaxed">
                <p>
                  A medical device entrepreneur, executive, and AI practitioner with over 25 years of research, development, and operational experience. Julian is a biochemist and mechanical design engineer who holds an EPSRC Total Technology PhD in Microsystems Technology from Cardiff University, with foundational work in microfluidics and MEMS — sponsored by GSK and recognised with an EPSRC Researcher-in-Residence Award. The first non-US graduate of NASA's International Space School, he also served as an educator in miniaturised technologies for space travel.
                </p>
                <p>
                  Julian founded Cellnovo, where he conceptualised, architected, and designed the world's first connected, wearable diabetes management system — and led its technical architecture through four successive platform generations between 2012 and 2018, from a proprietary Windows handset with ANT wireless protocol through to Android and locked mobile phone platforms with Roche BGM integration across international markets. Spanning roles in product design, manufacturing, quality assurance, regulatory affairs, and IP strategy, he served as Chief Science Officer through CE Mark certification and the company's IPO on Euronext Markets in July 2015. Julian was instrumental in raising capital across all stages of the company's growth: seed funding of over £1M, a £15M Series A, a £30M Series B led by Edmond de Rothschild Investment Partners, a €31.5M IPO on Euronext Paris, a subsequent €17.5M capital increase, and post-IPO debt facilities from the European Investment Bank and Kreos Capital — total reported fundraising of at least €80M, with total capital raised across all rounds exceeding $100M. He is a named inventor on approximately 17 patents across five companies spanning more than 20 years, covering connected device architecture, wearable biosensors, microactuators, sensor technologies, and AI/ML prediction systems.
                </p>
                <p>
                  Julian's AI and machine learning work is substantive and current. He co-invented and filed WO 2024/200999 A1 — a personalised metabolic ML prediction system using Extra Trees regression and digital metabolic twin architecture — and holds a separate PCT on computer vision-based food identification and macronutrient estimation. He has applied AI in clinical and commercial contexts for over a decade, from a bolus recommender system deployed at Cellnovo to modern generative and agentic AI. His live deployed applications include <a href="https://www.mybioblueprint.ai" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 transition-colors">www.mybioblueprint.ai</a>, a personalised genomics and health platform, and <a href="https://www.interest-ed.com" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 transition-colors">www.interest-ed.com</a>.
                </p>
                <p>
                  Through P2P Digital, Julian works with medtech and digital health companies on technical due diligence, expert witness matters, AI audits — assessing AI/ML system readiness, model governance, data quality, and regulatory alignment — and the design and development of agentic AI applications for regulated-industry clients. He also founded Glucose Republic, a CGM-adjacent metabolic wellness company in the UK, and serves as Entrepreneur in Residence at Biodesign Australia, mentoring early-stage founders through the Stanford Biodesign methodology. Currently he serves as Chief Operations Officer of VitalTrace in Perth, an Australian biosensor company developing connected fetal and neonatal monitoring systems. His six peer-reviewed publications span diabetes technology, clinical decision support, and microsystems engineering.
                </p>
              </div>

              {/* Social row */}
              <div className="mt-8 flex gap-4">
                <a
                  href="mailto:julian.shapley@p2pdigital.com.au"
                  className="bg-white/[0.06] p-3 rounded-xl border border-white/[0.06] hover:bg-white/[0.1] text-slate-400 hover:text-white transition-colors duration-200"
                >
                  <Mail className="w-5 h-5" />
                </a>
                <a
                  href="https://www.linkedin.com/in/julian-shapley/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/[0.06] p-3 rounded-xl border border-white/[0.06] hover:bg-white/[0.1] text-slate-400 hover:text-white transition-colors duration-200"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="/julian-shapley-cv.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/[0.06] p-3 rounded-xl border border-white/[0.06] hover:bg-white/[0.1] text-slate-400 hover:text-white transition-colors duration-200"
                >
                  <FileDown className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ACHIEVEMENTS ── */}
      <section className="py-24 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="uppercase tracking-[0.25em] text-cyan-400 font-mono text-sm mb-4">
            Track Record
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Proven Impact</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {achievements.map((a) => (
              <div
                key={a.title}
                className="rounded-2xl border border-white/[0.06] bg-white/[0.03] backdrop-blur-md p-8 text-center"
              >
                <span className="text-4xl font-display font-bold bg-gradient-to-r from-cyan-400 to-indigo-400 bg-clip-text text-transparent">
                  {a.value}
                </span>
                <h3 className="text-white font-display font-semibold mt-2">{a.title}</h3>
                <p className="text-slate-400 text-sm mt-1">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EXPERTISE ── */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <p className="uppercase tracking-[0.25em] text-cyan-400 font-mono text-sm mb-4">
              Expertise
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
              Deep Domain Knowledge
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {expertiseCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="group rounded-2xl border border-white/[0.06] bg-white/[0.03] backdrop-blur-md p-8 hover:bg-white/[0.06] transition-colors duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6">
                    <Icon className="text-cyan-400 w-6 h-6" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-3">{card.title}</h3>
                  <p className="text-slate-400 mb-4">{card.desc}</p>
                  <ul className="space-y-2">
                    {card.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
                        <span className="text-slate-300 text-sm">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className="py-24 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="uppercase tracking-[0.25em] text-cyan-400 font-mono text-sm mb-4">
            Our Philosophy
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
            Innovation with Integrity
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {values.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className="rounded-2xl border border-white/[0.06] bg-white/[0.03] backdrop-blur-md p-8"
                >
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6 mx-auto">
                    <Icon className="text-cyan-400 w-6 h-6" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-3">{v.title}</h3>
                  <p className="text-slate-400">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="rounded-3xl bg-gradient-to-br from-cyan-500/10 to-indigo-500/10 border border-white/[0.06] backdrop-blur-md p-12 sm:p-16 text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
              Let&rsquo;s Build Something Extraordinary
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl mx-auto">
              Ready to turn bold ideas into real-world impact? Get in touch and let&rsquo;s start the conversation.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold hover:opacity-90 transition-opacity duration-200"
            >
              Get in Touch
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default About;
